import * as React from 'react';

import { Button, ButtonGroup, Menu, MenuItem, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

// Shared primitives for tool-call cards. Lives outside ChatToolCall.tsx so
// specialised cards (e.g. AddPropertyImagesToolCard) and the generic card can
// both import them without a circular dependency — ChatToolCall renders the
// specialised cards, so anything they need back from it must live here.

export type ToolPart = {
  toolName: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  approval?: { id: string; approved?: boolean; reason?: string };
};

export type Status =
  | 'running'
  | 'done'
  | 'error'
  | 'approval-requested'
  | 'approval-responded'
  | 'denied';

export const statusOf = (part: ToolPart): Status => {
  if (part.state === 'output-error' || part.errorText != null) {
    return 'error';
  }
  if (part.state === 'output-available') {
    return 'done';
  }
  if (part.state === 'approval-requested') {
    return 'approval-requested';
  }
  if (part.state === 'approval-responded') {
    return 'approval-responded';
  }
  if (part.state === 'output-denied') {
    return 'denied';
  }
  return 'running';
};

export type ApprovalResponseHandler = (
  approvalId: string,
  approved: boolean,
  options?: {
    // "Approve all": also auto-approve every subsequent approval request in
    // this chat session, so the agent can chain mutations unhampered.
    approveAll?: boolean;
  },
) => void;

// Deny / Approve / Approve-all row for a pending human-in-the-loop approval.
// Shared by the generic tool card and specialised cards (e.g.
// AddPropertyImagesToolCard).
export const ApprovalActions = ({
  approvalId,
  onApprovalResponse,
  approveDisabled = false,
}: {
  approvalId: string;
  onApprovalResponse: ApprovalResponseHandler;
  approveDisabled?: boolean;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const [approveMenuAnchor, setApproveMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 10,
      }}
    >
      <Button
        size="small"
        variant="text"
        color="inherit"
        sx={{ color: colors.grey600 }}
        onClick={() => onApprovalResponse(approvalId, false)}
      >
        {t('Deny')}
      </Button>
      <ButtonGroup size="small" variant="contained" disableElevation>
        <Button
          disabled={approveDisabled}
          onClick={() => onApprovalResponse(approvalId, true)}
        >
          {t('Approve')}
        </Button>
        <Button
          disabled={approveDisabled}
          aria-haspopup="menu"
          aria-label={t('More approval options')}
          onClick={e => setApproveMenuAnchor(e.currentTarget)}
          sx={{ px: 0.5, minWidth: 0 }}
        >
          <ChevronDown size={14} />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={approveMenuAnchor}
        open={approveMenuAnchor != null}
        onClose={() => setApproveMenuAnchor(null)}
      >
        <MenuItem
          disabled={approveDisabled}
          onClick={() => {
            setApproveMenuAnchor(null);
            onApprovalResponse(approvalId, true, {
              approveAll: true,
            });
          }}
        >
          <div>
            {t('Approve all')}
            <Typography
              variant="caption"
              sx={{ display: 'block', color: colors.grey500 }}
            >
              {t('Run further changes in this chat without asking')}
            </Typography>
          </div>
        </MenuItem>
      </Menu>
    </div>
  );
};
